/**
 * Chunked isometric terrain renderer.
 *
 * Each CHUNK_SIZE×CHUNK_SIZE block of tiles is drawn once into a RenderTexture
 * and displayed as a single Sprite. A 256×256 map (65k tiles) therefore costs
 * ~256 sprites instead of 65k Graphics — and each frame we only toggle
 * `visible` on the chunks intersecting the camera viewport (culling). Chunks are
 * re-baked lazily only when their tiles change (dirty flag), which later sprints
 * (building placement, seasons) will use.
 */
import { Container, Graphics, Sprite, RenderTexture } from 'pixi.js';
import type { Renderer } from 'pixi.js';
import { gridToWorld, ELEVATION_STEP, TILE_HALF_WIDTH, TILE_HALF_HEIGHT, CHUNK_SIZE } from '@settlers/shared';
import type { GameMap } from '@settlers/engine';
import type { AssetProvider } from '../assets/AssetRegistry.js';
import type { Camera } from '../camera/Camera.js';
import { chunkWorldBounds, chunkCountX, chunkCountY, diamondPoints } from './geometry.js';
import type { WorldBox } from './geometry.js';

interface Chunk {
  readonly cx: number;
  readonly cy: number;
  readonly bounds: WorldBox;
  readonly sprite: Sprite;
  texture: RenderTexture;
  dirty: boolean;
}

export class IsoRenderer {
  /** World-space container; the camera transform is applied to this each frame. */
  readonly stage = new Container();
  private readonly chunks: Chunk[] = [];
  private readonly cols: number;
  private readonly rows: number;
  private visibleChunks = 0;

  constructor(
    private readonly renderer: Renderer,
    private readonly map: GameMap,
    private readonly assets: AssetProvider,
  ) {
    this.stage.sortableChildren = true;
    this.cols = chunkCountX(map);
    this.rows = chunkCountY(map);
    this.buildChunks();
  }

  private buildChunks(): void {
    for (let cy = 0; cy < this.rows; cy++) {
      for (let cx = 0; cx < this.cols; cx++) {
        const bounds = chunkWorldBounds(this.map, cx, cy);
        const w = Math.max(1, Math.ceil(bounds.maxX - bounds.minX));
        const h = Math.max(1, Math.ceil(bounds.maxY - bounds.minY));
        const texture = RenderTexture.create({ width: w, height: h, resolution: 1 });
        const sprite = new Sprite(texture);
        sprite.position.set(bounds.minX, bounds.minY);
        // Iso draw order between chunks: further back (smaller cx+cy) drawn first.
        sprite.zIndex = cx + cy;
        this.stage.addChild(sprite);
        const chunk: Chunk = { cx, cy, bounds, sprite, texture, dirty: true };
        this.chunks.push(chunk);
        this.bake(chunk);
      }
    }
  }

  /** Re-draw a chunk's tiles into its texture. */
  private bake(chunk: Chunk): void {
    const g = new Graphics();
    const x0 = chunk.cx * CHUNK_SIZE;
    const y0 = chunk.cy * CHUNK_SIZE;
    const x1 = Math.min(x0 + CHUNK_SIZE, this.map.width);
    const y1 = Math.min(y0 + CHUNK_SIZE, this.map.height);

    // Draw back-to-front by isometric depth (x + y ascending).
    const tiles: Array<{ x: number; y: number }> = [];
    for (let y = y0; y < y1; y++) {
      for (let x = x0; x < x1; x++) tiles.push({ x, y });
    }
    tiles.sort((a, b) => a.x + a.y - (b.x + b.y));

    for (const { x, y } of tiles) {
      this.drawTile(g, x, y, chunk.bounds);
    }

    this.renderer.render({ container: g, target: chunk.texture, clear: true });
    g.destroy();
    chunk.dirty = false;
  }

  private drawTile(g: Graphics, x: number, y: number, origin: WorldBox): void {
    const elev = this.map.elevationAt(x, y);
    const c = gridToWorld(x, y, elev);
    const cx = c.x - origin.minX;
    const cy = c.y - origin.minY;
    const faces = this.assets.tileFaces(this.map.terrainAt(x, y));

    // Side faces fake the elevation "block".
    if (elev > 0) {
      const drop = elev * ELEVATION_STEP;
      // Left face: left -> bottom, extruded down.
      g.poly([
        cx - TILE_HALF_WIDTH, cy,
        cx, cy + TILE_HALF_HEIGHT,
        cx, cy + TILE_HALF_HEIGHT + drop,
        cx - TILE_HALF_WIDTH, cy + drop,
      ]).fill(faces.left);
      // Right face: right -> bottom, extruded down.
      g.poly([
        cx + TILE_HALF_WIDTH, cy,
        cx, cy + TILE_HALF_HEIGHT,
        cx, cy + TILE_HALF_HEIGHT + drop,
        cx + TILE_HALF_WIDTH, cy + drop,
      ]).fill(faces.right);
    }

    // Top diamond.
    g.poly(diamondPoints(cx, cy)).fill(faces.top);
    // Subtle edge for tile definition.
    g.poly(diamondPoints(cx, cy)).stroke({ width: 1, color: 0x000000, alpha: 0.06 });

    // Deposit marker.
    const deposit = this.map.depositAt(x, y);
    const dColor = this.assets.depositColor(deposit.type);
    if (dColor !== null) {
      g.circle(cx, cy - 2, 4).fill({ color: dColor, alpha: 0.9 });
    }
  }

  /** Toggle chunk visibility against the camera viewport; returns visible count. */
  cull(camera: Camera): number {
    const view = camera.visibleWorldRect();
    const vx1 = view.x + view.w;
    const vy1 = view.y + view.h;
    let visible = 0;
    for (const chunk of this.chunks) {
      const b = chunk.bounds;
      const isVisible = b.maxX >= view.x && b.minX <= vx1 && b.maxY >= view.y && b.minY <= vy1;
      chunk.sprite.visible = isVisible;
      if (isVisible) visible++;
    }
    this.visibleChunks = visible;
    return visible;
  }

  get stats(): { chunks: number; visibleChunks: number } {
    return { chunks: this.chunks.length, visibleChunks: this.visibleChunks };
  }

  /** Total world-space extent of the whole map (for camera bounds). */
  worldExtent(): WorldBox {
    // Corners of the diamond map: extremes come from the four grid corners.
    const corners = [
      gridToWorld(0, 0),
      gridToWorld(this.map.width - 1, 0),
      gridToWorld(0, this.map.height - 1),
      gridToWorld(this.map.width - 1, this.map.height - 1),
    ];
    const box: WorldBox = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
    for (const c of corners) {
      box.minX = Math.min(box.minX, c.x - TILE_HALF_WIDTH);
      box.maxX = Math.max(box.maxX, c.x + TILE_HALF_WIDTH);
      box.minY = Math.min(box.minY, c.y - TILE_HALF_HEIGHT);
      box.maxY = Math.max(box.maxY, c.y + TILE_HALF_HEIGHT);
    }
    return box;
  }

  destroy(): void {
    for (const chunk of this.chunks) {
      chunk.texture.destroy(true);
      chunk.sprite.destroy();
    }
    this.chunks.length = 0;
    this.stage.destroy({ children: true });
  }
}
